/**
 * Coinflow Client-Side Tokenization
 * 
 * Uses Coinflow's React SDK components (@coinflowlabs/react) for client-side tokenization.
 * This approach maintains PCI compliance without requiring PCI-DSS certification.
 * 
 * The tokenization is handled by CoinflowCardNumberInput and CoinflowCvvInput components,
 * which provide a getToken() method to retrieve the tokenized card data.
 */

import { CoinflowEnvs } from '@coinflowlabs/react';
import { apiClient } from './api-client';

/**
 * Product type for chargeback protection data
 * Must be 'gameOfSkill' to match Coinflow's expected format for social casino
 */
export const COINFLOW_PRODUCT_TYPE = 'gameOfSkill' as const;

/**
 * Standard description for chargeback protection data
 * Matches the format expected by Coinflow
 */
export const COINFLOW_PRODUCT_DESCRIPTION = 'The user is purchasing a package that grants them coins based on their selected package.' as const;

export interface CardData {
  number: string;
  cvv: string;
  expMonth: string;
  expYear: string;
  zipCode: string;
}

export interface BankAccountData {
  accountNumber: string;
  routingNumber: string;
  accountType: 'checking' | 'savings';
  accountHolderName: string;
}

export interface CoinflowCardTokenResponse {
  token: string;
  firstSix?: string;
  lastFour?: string;
  referenceNumber?: string;
  success?: boolean;
  error?: string;
  message?: string;
}

export class CoinflowTokenization {
  private merchantId: string;
  private environment: CoinflowEnvs;
  private apiBaseUrl: string;

  constructor(merchantId: string, environment: CoinflowEnvs = 'sandbox') {
    this.merchantId = merchantId;
    this.environment = environment;
    this.apiBaseUrl = environment === 'sandbox'
      ? 'https://api-sandbox.coinflow.cash/api'
      : 'https://api.coinflow.cash/api';
  }

  /**
   * Get 3DS parameters from user's browser session
   * Required for 3DS authentication
   */
  static get3DSParams() {
    return {
      colorDepth: window.screen.colorDepth,
      screenHeight: window.screen.height,
      screenWidth: window.screen.width,
      timeZone: -new Date().getTimezoneOffset(),
    };
  }

  /**
   * Tokenize card using Coinflow React SDK components
   * 
   * NOTE: This method should not be called directly. Instead, use CoinflowCardNumberInput
   * and CoinflowCvvInput components which provide a getToken() method.
   * 
   * This method is kept for backward compatibility but will throw an error.
   * Use the React components directly in your forms.
   */
  async tokenizeCard(cardData: CardData): Promise<string> {
    throw new Error(
      'Direct tokenization is not supported. Please use CoinflowCardNumberInput and CoinflowCvvInput ' +
      'components from @coinflowlabs/react. Call getToken() on the card input ref to get the tokenized card.'
    );
  }

  /**
   * Tokenize bank account for ACH
   */
  async tokenizeBankAccount(bankData: BankAccountData): Promise<string> {
    try {
      // TODO: Implement actual client-side bank account tokenization
      // This would use Coinflow's tokenization endpoint
      
      const url = `${this.apiBaseUrl}/tokenize/bank`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          accountNumber: bankData.accountNumber,
          routingNumber: bankData.routingNumber,
          accountType: bankData.accountType,
          accountHolderName: bankData.accountHolderName,
        }),
      });

      if (!response.ok) {
        throw new Error('Bank account tokenization failed');
      }

      const data = await response.json();
      return data.token;
    } catch (error) {
      throw error;
    }
  }
}

